import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, FolderKey, Loader2, Search, ShieldCheck, X } from 'lucide-react';
import { AdminSidebar } from '../../components/layout/AdminSidebar';
import { ProjectMark } from '../../components/project/ProjectMark';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select } from '../../components/ui/input';
import { api, type AdminProject, type AdminUser, type MemberRole } from '../../lib/api';
import { cn } from '../../lib/utils';

/** '' is "no access" — no membership row at all, which is a different thing from VIEWER. */
type RoleChoice = MemberRole | '';

const ROLE_OPTIONS: Array<{ value: RoleChoice; label: string }> = [
  { value: '', label: 'No access' },
  { value: 'VIEWER', label: 'Viewer' },
  { value: 'MEMBER', label: 'Member' },
  { value: 'ADMIN', label: 'Admin' },
];

function UserRow({ user, active, onClick }: { user: AdminUser; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left px-3 py-2.5 rounded-md transition-colors',
        active ? 'bg-surface-muted' : 'hover:bg-surface-subtle',
      )}
    >
      <div className="flex items-center gap-1.5">
        <span className={cn('text-xs font-medium truncate flex-1', active ? 'text-ink' : 'text-ink-secondary')}>
          {user.name ?? user.email}
        </span>
        {user.isSystemAdmin && (
          <span title="System admin" className="flex-shrink-0 flex items-center">
            <ShieldCheck size={11} className="text-ink-faint" />
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5 mt-0.5">
        {user.name && <span className="text-[10px] text-ink-faint truncate flex-1">{user.email}</span>}
        {user.accountStatus !== 'APPROVED' && (
          <span className="text-[9px] font-medium text-warning-text flex-shrink-0">
            {user.accountStatus === 'WAITING_APPROVAL' ? 'pending' : 'suspended'}
          </span>
        )}
      </div>
    </button>
  );
}

/**
 * One user's role in every project.
 *
 * Held locally and saved in one PUT, like Feature Permissions: the server replaces the user's whole
 * set, so a save is one decision and a dropped request cannot leave half of it applied.
 */
function AccessEditor({
  user, projects, onSaved,
}: {
  user: AdminUser;
  projects: AdminProject[];
  /** Member and admin counts on every row move with a save, so the page refetches them. */
  onSaved: () => void;
}) {
  const [baseline, setBaseline] = useState<Map<string, MemberRole>>(new Map());
  const [roles, setRoles] = useState<Map<string, MemberRole>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [filter, setFilter] = useState('');
  const [onlyGranted, setOnlyGranted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSaved(false);
    api.admin
      .getUserProjectRoles(user.id)
      .then((rows) => {
        if (cancelled) return;
        const map = new Map(rows.map((r) => [r.projectId, r.role]));
        setBaseline(map);
        setRoles(new Map(map));
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [user.id]);

  const changed = useMemo(() => {
    const ids = new Set([...baseline.keys(), ...roles.keys()]);
    return [...ids].filter((id) => baseline.get(id) !== roles.get(id));
  }, [baseline, roles]);
  const dirty = changed.length > 0;

  const setRole = (projectId: string, role: RoleChoice) => {
    setSaved(false);
    setRoles((prev) => {
      const next = new Map(prev);
      if (role) next.set(projectId, role);
      else next.delete(projectId);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const rows = await api.admin.setUserProjectRoles(
        user.id,
        [...roles].map(([projectId, role]) => ({ projectId, role })),
      );
      const map = new Map(rows.map((r) => [r.projectId, r.role]));
      setBaseline(map);
      setRoles(new Map(map));
      onSaved();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return projects.filter(
      (p) =>
        (!onlyGranted || roles.has(p.id) || baseline.has(p.id)) &&
        (!needle || p.name.toLowerCase().includes(needle) || p.path.toLowerCase().includes(needle)),
    );
  }, [projects, filter, onlyGranted, roles, baseline]);

  // Depth is indented relative to the shallowest row, so the organization root sits flush left.
  const minDepth = projects.reduce((min, p) => Math.min(min, p.depth), Infinity);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-7 pt-6 pb-4 border-b border-border-subtle flex-shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-base font-bold text-ink leading-tight">{user.name ?? user.email}</h1>
            {user.name && <p className="text-xs text-ink-faint mt-0.5">{user.email}</p>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge>{baseline.size} project{baseline.size === 1 ? '' : 's'}</Badge>
            {user.isSystemAdmin && <Badge variant="info">System admin</Badge>}
          </div>
        </div>

        {user.accountStatus !== 'APPROVED' && (
          <p className="mt-3 text-[11px] text-warning-text bg-warning-bg border border-warning-ring rounded px-3 py-1.5 flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-px flex-shrink-0" />
            This account is {user.accountStatus === 'WAITING_APPROVAL' ? 'awaiting approval' : 'suspended'} and
            can't sign in, so these roles won't take effect until it's approved under Accounts.
          </p>
        )}

        <p className="text-xs text-ink-muted mt-3 max-w-xl leading-relaxed">
          Pick a role per project. Roles are per project: a role in a project is not a role in its
          sub-projects. Viewers read, members read and contribute, admins also manage the project's members.
        </p>

        <div className="flex items-center gap-3 mt-3">
          <div className="relative flex-1 max-w-xs">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter projects…"
              className="w-full pl-7 pr-3 py-1.5 text-xs bg-surface border border-border rounded-md outline-none focus:border-orange-400 placeholder:text-ink-faint text-ink"
            />
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-ink-muted cursor-pointer select-none">
            <input type="checkbox" checked={onlyGranted} onChange={(e) => setOnlyGranted(e.target.checked)} />
            Only projects with a role
          </label>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-7 py-4">
        {loading ? (
          <p className="text-xs text-ink-faint py-8 text-center">Loading…</p>
        ) : visible.length === 0 ? (
          <p className="text-xs text-ink-faint py-8 text-center">
            {onlyGranted && !filter ? 'No project roles yet — untick the filter to grant one.' : 'No matching projects'}
          </p>
        ) : (
          <div className="border border-border rounded-lg divide-y divide-border-subtle overflow-hidden">
            {visible.map((project) => {
              const role = roles.get(project.id) ?? '';
              const isChanged = baseline.get(project.id) !== roles.get(project.id);
              return (
                <div
                  key={project.id}
                  className={cn('flex items-center gap-3 px-4 py-2', isChanged && 'bg-orange-50/60 dark:bg-orange-500/5')}
                >
                  <div
                    className="flex items-center gap-2 min-w-0 flex-1"
                    style={{ paddingLeft: filter ? 0 : (project.depth - minDepth) * 18 }}
                  >
                    <ProjectMark project={project} />
                    <div className="min-w-0">
                      <p className={cn('text-xs truncate', role ? 'text-ink font-medium' : 'text-ink-secondary')}>
                        {project.name}
                      </p>
                      <p className="text-[10px] font-mono text-ink-faint truncate">{project.path}</p>
                    </div>
                  </div>
                  {/* Server counts, so they describe what is saved — the warning is about the
                      project as it stands, not about the edit in progress. */}
                  {project.members === 0 ? (
                    <span className="text-[10px] text-ink-faint flex-shrink-0">no members</span>
                  ) : project.admins === 0 ? (
                    <span className="text-[10px] font-medium text-warning-text flex-shrink-0">no admin</span>
                  ) : (
                    <span className="text-[10px] text-ink-faint flex-shrink-0">
                      {project.members} member{project.members === 1 ? '' : 's'}
                    </span>
                  )}
                  <Select
                    value={role}
                    disabled={saving}
                    onChange={(e) => setRole(project.id, e.target.value as RoleChoice)}
                    className={cn('py-1 text-xs w-28 flex-shrink-0', !role && 'text-ink-faint')}
                  >
                    {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Select>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="px-7 py-3 border-t border-border-subtle flex-shrink-0 flex items-center justify-between gap-3 bg-surface-subtle">
        <div className="min-w-0">
          {error && (
            <p className="text-[11px] text-danger-text flex items-center gap-1.5">
              <X size={11} className="flex-shrink-0" />{error}
            </p>
          )}
          {!error && saved && (
            <p className="text-[11px] text-green-600 dark:text-green-400 flex items-center gap-1.5">
              <Check size={11} />Saved — takes effect on their next request
            </p>
          )}
          {!error && !saved && dirty && (
            <p className="text-[11px] text-ink-muted">
              {changed.length} change{changed.length === 1 ? '' : 's'} pending
            </p>
          )}
          {!error && !saved && !dirty && (
            <p className="text-[11px] text-ink-faint">
              {baseline.size === 0 ? 'No project roles' : `Role in ${baseline.size} project${baseline.size === 1 ? '' : 's'}`}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button size="sm" onClick={() => { setRoles(new Map(baseline)); setError(null); }} disabled={saving || !dirty}>
            Revert
          </Button>
          <Button variant="primary" size="sm" onClick={save} disabled={saving || !dirty}>
            {saving ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Check size={12} className="mr-1.5" />}
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminProjectAccess() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.admin.listUsers(), api.admin.listProjects()])
      .then(([userList, projectList]) => {
        setUsers(userList);
        setProjects(projectList);
        setSelectedId((current) => current ?? userList[0]?.id ?? null);
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const refreshProjects = useCallback(() => {
    api.admin.listProjects().then(setProjects).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(
      (u) => u.email.toLowerCase().includes(needle) || (u.name ?? '').toLowerCase().includes(needle),
    );
  }, [users, filter]);

  const selected = users.find((u) => u.id === selectedId) ?? null;

  return (
    <div className="flex h-screen overflow-hidden bg-surface">
      <AdminSidebar />

      <div className="pl-56 flex h-full w-full overflow-hidden">
        <div className="w-72 flex-shrink-0 h-full bg-card border-r border-border flex flex-col overflow-hidden">
          <div className="px-3 py-3 border-b border-border space-y-2.5">
            <div className="flex items-center gap-2">
              <FolderKey size={13} className="text-ink-faint" />
              <span className="text-sm font-semibold text-ink">Project Access</span>
            </div>
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter users…"
                className="w-full pl-7 pr-3 py-1.5 text-xs bg-surface rounded-md border-none outline-none placeholder:text-ink-faint text-ink"
              />
            </div>
            <p className="text-[10px] text-ink-faint">
              {users.length} user{users.length === 1 ? '' : 's'} · {projects.length} project{projects.length === 1 ? '' : 's'}
            </p>
          </div>

          <div className="flex-1 overflow-y-auto px-2 py-2">
            {loading ? (
              <p className="px-2 py-4 text-xs text-ink-faint">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="px-2 py-4 text-xs text-ink-faint">No matching users</p>
            ) : (
              <div className="space-y-0.5">
                {filtered.map((user) => (
                  <UserRow key={user.id} user={user} active={user.id === selectedId} onClick={() => setSelectedId(user.id)} />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 h-full overflow-hidden bg-card min-w-0">
          {error ? (
            <div className="p-7">
              <p className="text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-2">{error}</p>
            </div>
          ) : selected ? (
            // Keyed so switching users drops the previous user's unsaved edits instead of
            // carrying them across.
            <AccessEditor key={selected.id} user={selected} projects={projects} onSaved={refreshProjects} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-xs text-ink-faint">Select a user to edit their project roles.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
